export enum ProposalStatus {
  AwaitingSubmission = "AwaitingSubmission",
  AwaitingApprovalByClient = "AwaitingApprovalByClient",
  AwaitingApprovalByTeamLeader = "AwaitingApprovalByTeamLeader",
  AwaitingApprovalByVendors = "AwaitingApprovalByVendors",
  AwaitingApprovalByTeam = "AwaitingApprovalByTeam",
  AwaitingHire = "AwaitingHire",
  RevisionRequested = "RevisionRequested",
  // The server's own status; without it a refused revision read as no status, and so as a draft.
  RevisionRejected = "RevisionRejected",
  Archived = "Archived"
}

export enum HumanReadableProposalStatus {
  AwaitingSubmission = 'Awaiting Submission',
  AwaitingApprovalByClient = 'Awaiting Client Approval',
  AwaitingApprovalByTeamLeader = 'Awaiting Team Leader Approval ',
  AwaitingApprovalByVendors = 'Awaiting Vendor Approval ',
  AwaitingApprovalByTeam = 'Awaiting Team Approval',
  AwaitingHire = 'Awaiting Hire',
  AwaitingFurtherRevision = 'Awaiting Further Revision',
  RevisionRequested = 'Revision Requested',
  RevisionRejected = 'Revision Rejected',
  Hired = 'Hired',
  Canceled = 'Canceled',
  // Archived because the client hired another firm for the same work (#576).
  AwardedElsewhere = 'Closed: Awarded Elsewhere',
  Archived = 'Archived',
  Unknown = 'Unknown'
}

/**
 * The statuses `Proposal.displayStatus` shows in place of the stored one when the proposal waits on
 * the reader. The status chip's tone reads them too (`proposalStatusTone`), so the two cannot drift.
 */
export const AWAITING_YOUR_APPROVAL = "Awaiting Your Approval";
export const AWAITING_YOUR_REVIEW = "Awaiting Your Review";

export const proposalActor = {
  Client: 0,
  Vendor: 1,
  TeamLeader: 2,
  TeamMember: 3,
  Unknown: 4
};

export enum ProposalAction {
  Submit = "Submit",
  Approve = "Approve",
  Reject = "Reject",
  Revise = "Revise",
  Hire = "Hire",
  Delete = "Delete",
  Cancel = "Cancel",
  Edit = "Edit",
  Review = "Review"
}

export enum ProposalEvent {
  Drafted = "Drafted",
  SubmittedByCreator = "SubmittedByCreator",
  SubmittedByClientTeamLeader = "SubmittedByClientTeamLeader",
  SubmittedByVendorTeamLeader = "SubmittedByVendorTeamLeader",
  SubmittedByAdditionalVendor = "SubmittedByAdditionalVendor",
  SubmittedByVendorTeamMember = "SubmittedByVendorTeamMember",
  ApprovedByVendorTeamLeader = "ApprovedByVendorTeamLeader",
  ApprovedByAdditionalVendor = "ApprovedByAdditionalVendor",
  ApprovedByVendors = "ApprovedByVendors",
  ApprovedByVendorTeamMember = "ApprovedByVendorTeamMember",
  ApprovedByVendorTeam = "ApprovedByVendorTeam",
  ApprovedByClientTeamLeader = "ApprovedByClientTeamLeader",
  Hired = "Hired",
  SubmittedAsRevision = "SubmittedAsRevision",
  RevisionRequested = "RevisionRequested",
  RevisionRejected = "RevisionRejected",
  Revised = "Revised",
  RejectedByClientTeamLeader = "RejectedByClientTeamLeader",
  RejectedByAdditionalVendor = "RejectedByAdditionalVendor",
  RejectedByVendorTeamLeader = "RejectedByVendorTeamLeader",
  RejectedByVendorTeamMember = "RejectedByVendorTeamMember",
  Canceled = "Canceled",
  Finished = "Finished",
  Deleted = "Deleted",
  AwardedElsewhere = "AwardedElsewhere",
}

export enum ProposalBoxType {
  Draft = "Draft",
  DraftReview = "DraftReview",
  DraftAuthored = "DraftAuthored",
  Inbox = "Inbox",
  Outbox = "Outbox",
  ActiveReview = "ActiveReview",
  AllActive = "AllActive",
  Archive = "Archive",
}

/**
 * Where a proposal is in its life, as `GET /work/proposals` filters it (`Work.Proposals.ProposalStage`).
 * Every status is in exactly one; a list with no stage shows the drafts and active ones (#6473).
 */
export enum ProposalStage {
  Draft = "Draft",
  Active = "Active",
  Archived = "Archived",
}

/**
 * What a proposal needs from the person reading it (`Work.Proposals.ProposalMove`): the list
 * filters by it, and tints the row by the one the server says applies (#6473).
 */
export enum ProposalMove {
  YourMove = "YourMove",
  YourReview = "YourReview",
  TheirMove = "TheirMove",
}

/**
 * How an archived proposal ended (`Work.Proposals.ProposalOutcome`): the archive filters by it, and
 * each row reports the one the server read from its history (#6576). An archived proposal that was
 * rejected or revised has none.
 */
export enum ProposalOutcome {
  Hired = "Hired",
  Canceled = "Canceled",
  AwardedElsewhere = "AwardedElsewhere",
}

export enum ProposalContext {
  Client = "Client",
  Vendor = "Vendor",
  None = "None",
}

export enum ProposalOrderField {
  Created = "Created",
  Name = "Name",
  Start = "Start",
  End = "End",
  ResponseDueBy = "ResponseDueBy",
  Discount = "Discount",
  Updated = "Updated",
  Deleted = "Deleted",
  /** By request, so the proposals one request produced are next to each other (#6460). */
  RFP = "RFP",
}
